import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'pos-katalabaineis-tatouaz-molynsi-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί;", acceptedAnswer: { "@type": "Answer", text: "Τα βασικά σημάδια είναι κοκκίνισμα που απλώνεται και εντείνεται, αυξανόμενο πρήξιμο, έντονος πόνος που δεν υποχωρεί, πύον με άσχημη οσμή, έντονη ζέστη, πυρετός και κόκκινες γραμμές που εκτείνονται. Η μόλυνση χειροτερεύει, ενώ η φυσιολογική επούλωση βελτιώνεται." } },
    { "@type": "Question", name: "Είναι φυσιολογικό να κοκκινίζει το τατουάζ μου τις πρώτες μέρες;", acceptedAnswer: { "@type": "Answer", text: "Ναι, ελαφρύ κοκκίνισμα τις πρώτες μέρες είναι φυσιολογικό. Το φυσιολογικό κοκκίνισμα μειώνεται σταδιακά. Αν αντίθετα εντείνεται ή απλώνεται, χρειάζεται προσοχή." } },
    { "@type": "Question", name: "Τι είναι το φυσιολογικό έκκριμα και τι είναι πύον;", acceptedAnswer: { "@type": "Answer", text: "Το φυσιολογικό έκκριμα είναι διαυγές ή ελαφρώς ροζ, υγρό, χωρίς άσχημη οσμή. Το πύον είναι πυκνό, κίτρινο ή κιτρινοπράσινο, με άσχημη γλυκερή οσμή. Το πύον είναι σαφές σημάδι μόλυνσης." } },
    { "@type": "Question", name: "Τι να κάνω αν υποψιάζομαι μόλυνση στο τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Ενημέρωσε τον καλλιτέχνη σου και στείλε φωτογραφίες. Αν τα συμπτώματα είναι έντονα, με πύον, πυρετό, έντονο πόνο ή κόκκινες γραμμές, πήγαινε σε γιατρό άμεσα. Μη βάζεις τυχαία προϊόντα και μη σκας φουσκάλες." } },
    { "@type": "Question", name: "Πού μπορώ να κάνω τατουάζ στην Αθήνα με ασφάλεια;", acceptedAnswer: { "@type": "Answer", text: "Το 210 Tattoo Athens τηρεί αυστηρά πρωτόκολλα υγιεινής, δουλεύει με στείρο εξοπλισμό και δίνει αναλυτικές οδηγίες μεταφροντίδας. Στείλε μήνυμα στο @210tattoo στο Instagram για να κλείσεις ραντεβού." } },
  ],
};

export default function PosKatalabaineisTatouazMolynsiAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς Καταλαβαίνεις ότι ένα Τατουάζ έχει Μολυνθεί",
      "description": "Οδηγός για την Αθήνα: πώς ξεχωρίζεις τη φυσιολογική επούλωση από τη μόλυνση, ποια σημάδια σε βάζουν σε επιφυλακή, πότε να δεις γιατρό και πώς προλαμβάνεις τη μόλυνση.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-infection-guide-faq', faqSchema);
    inject('schema-infection-guide-article', articleSchema);
    return () => {
      document.getElementById('schema-infection-guide-faq')?.remove();
      document.getElementById('schema-infection-guide-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πώς Καταλαβαίνεις Μόλυνση σε Τατουάζ | 210 Tattoo Athens</title>
        <meta name="description" content="Πώς ξεχωρίζεις τη φυσιολογική επούλωση από τη μόλυνση σε ένα τατουάζ: τα σημάδια επιφυλακής, πότε να δεις γιατρό και πώς προλαμβάνεις τη μόλυνση." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Πώς Καταλαβαίνεις Μόλυνση σε Τατουάζ | 210 Tattoo Athens" />
        <meta property="og:description" content="Φυσιολογική επούλωση ή μόλυνση; Τα σημάδια, πότε να δεις γιατρό και πώς προλαμβάνεις." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πώς Καταλαβαίνεις Μόλυνση σε Τατουάζ | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τα σημάδια της μόλυνσης, πότε να δεις γιατρό και πώς προλαμβάνεις." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Μόλυνση σε Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πώς καταλαβαίνεις ότι</span>
              <br />
              <span className="text-white">ένα τατουάζ έχει μολυνθεί;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Κοκκίνισμα, ζέστη, ένα υγρό που βγαίνει από την περιοχή. Είναι όλα φυσιολογικά ή έχει μολυνθεί το τατουάζ σου; Δες πώς να ξεχωρίσεις τη φυσιολογική επούλωση από τη μόλυνση και τι να κάνεις σε κάθε περίπτωση.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Normal signs */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πρώτα τα φυσιολογικά, <span className="text-pink-500">μην πανικοβάλλεσαι</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το κλειδί για όλα τα φυσιολογικά σημάδια είναι μία λέξη: βελτίωση. Όλα βελτιώνονται μέρα με τη μέρα. Η μόλυνση κάνει το ακριβώς αντίθετο, χειροτερεύει.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Ελαφρύ κοκκίνισμα', desc: 'Τις πρώτες μέρες είναι φυσιολογικό η περιοχή να είναι κόκκινη. Το κοκκίνισμα μειώνεται σταδιακά κάθε μέρα.' },
                { title: 'Ελαφρύ πρήξιμο', desc: 'Λίγο πρήξιμο τις πρώτες σαράντα οκτώ ώρες είναι φυσιολογικό, ειδικά κοντά σε αρθρώσεις, και υποχωρεί.' },
                { title: 'Ζέστη στην περιοχή', desc: 'Η αίσθηση ζέστης, σαν ελαφρύ κάψιμο, είναι κομμάτι της φυσιολογικής φλεγμονής της πρώτης επούλωσης.' },
                { title: 'Διαυγές έκκριμα', desc: 'Τις πρώτες μέρες το τατουάζ βγάζει διαυγές, διάφανο υγρό, ίσως με ροζ απόχρωση από πλάσμα. Χωρίς άσχημη οσμή.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Infection signs */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τα σημάδια <span className="text-pink-500">της μόλυνσης</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Η μόλυνση δεν εμφανίζεται με ένα σημάδι, αλλά συνήθως με έναν συνδυασμό από τα παρακάτω.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Κοκκίνισμα που απλώνεται και εντείνεται', desc: 'Απλώνεται πέρα από το περίγραμμα του τατουάζ και γίνεται πιο έντονο. Αν την τέταρτη ή πέμπτη ημέρα είναι χειρότερο από την πρώτη, είναι σαφές σήμα.' },
                { title: 'Πρήξιμο που δεν υποχωρεί ή αυξάνεται', desc: 'Το πρησμένο δέρμα που είναι σκληρό και ζεστό μετά την τρίτη ή τέταρτη μέρα χρειάζεται προσοχή.' },
                { title: 'Αυξανόμενος, σφυγμικός πόνος', desc: 'Ο φυσιολογικός πόνος μειώνεται. Πόνος που αυξάνεται και είναι βαθύς, σαν να χτυπάει η καρδιά μέσα στο τατουάζ, είναι σοβαρό σημάδι.' },
                { title: 'Πύον', desc: 'Πυκνό, κίτρινο ή κιτρινοπράσινο υγρό με άσχημη οσμή. Σημαίνει βακτηριακή δραστηριότητα και είναι από τα πιο σαφή σημάδια μόλυνσης.' },
                { title: 'Πυρετός και γενική αδιαθεσία', desc: 'Πυρετός, ρίγη, εξάντληση μετά από πρόσφατο τατουάζ είναι επείγον. Η μόλυνση μπορεί να έχει εξαπλωθεί συστημικά.' },
                { title: 'Κόκκινες γραμμές που εκτείνονται', desc: 'Το πιο σοβαρό σημάδι. Λεπτές κόκκινες γραμμές που προχωράνε από το τατουάζ προς τα έξω. Πήγαινε σε γιατρό αμέσως.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Προσοχή στη διάκριση από αλλεργική αντίδραση: η αλλεργία συνήθως εντοπίζεται σε συγκεκριμένα χρώματα με έντονη φαγούρα και μικρές φουσκάλες, ενώ η μόλυνση επηρεάζει ολόκληρη την περιοχή με πύον και πόνο. Και οι δύο χρειάζονται εκτίμηση από ειδικό.
            </p>
          </div>
        </section>

        {/* When to see a doctor */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε να δεις γιατρό <span className="text-pink-500">και τι να κάνεις εν τω μεταξύ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ένας tattoo artist, όσο έμπειρος κι αν είναι, δεν είναι γιατρός. Μπορεί να σε βοηθήσει να καταλάβεις αν αυτό που βλέπεις είναι φυσιολογικό, αλλά δεν μπορεί να διαγνώσει και να θεραπεύσει μια μόλυνση. Πήγαινε σε γιατρό αν δεις πύον με άσχημη οσμή, κοκκίνισμα που εξαπλώνεται, έντονο αυξανόμενο πόνο, πυρετό ή κόκκινες γραμμές. Η έγκαιρη αντιμετώπιση είναι απλή, μια σειρά αντιβιωτικών, ενώ η καθυστέρηση μπορεί να οδηγήσει σε επιπλοκές.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Εν τω μεταξύ, ενημέρωσε τον καλλιτέχνη σου και στείλε φωτογραφίες. Μη βάλεις τυχαία προϊόντα ή αντιβιοτική αλοιφή, γιατί μπορεί να επιδεινώσουν την κατάσταση ή να δυσκολέψουν τη διάγνωση. Κράτα την περιοχή καθαρή και ξηρή, μη σκας φουσκάλες και παρακολούθησε τα συμπτώματα. Αν κάτι επιδεινώνεται γρήγορα, πήγαινε στα επείγοντα.
            </p>
          </div>
        </section>

        {/* Prevention */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να προλάβεις <span className="text-pink-500">τη μόλυνση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Οι μολύνσεις σε τατουάζ που γίνονται σωστά και φροντίζονται σωστά είναι σπάνιες. Η πρόληψη ξεκινάει από την πρώτη σου επιλογή, το στούντιο.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Διάλεξε σοβαρό στούντιο', desc: 'Καθαρός, οργανωμένος χώρος, στείρες βελόνες μιας χρήσης και εμφανείς πρακτικές υγιεινής. Στην Αθήνα υπάρχουν πολλές επιλογές, μη συμβιβάζεσαι.' },
                { title: 'Ακολούθησε τη μεταφροντίδα', desc: 'Δεν είναι πρόταση, είναι απαραίτητο. Ακολούθησε τις οδηγίες πιστά, όχι κατά προσέγγιση.' },
                { title: 'Πλένε τα χέρια σου', desc: 'Βρόμικα χέρια πάνω σε ανοιχτή πληγή είναι η πιο εύκολη διαδρομή για βακτήρια.' },
                { title: 'Απόφυγε θάλασσα και πισίνα', desc: 'Μέχρι την πλήρη επιφανειακή επούλωση. Στο καλοκαίρι της Αθήνας αυτό απαιτεί αυτοπειθαρχία, αλλά είναι κρίσιμο.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Η σωστή φροντίδα είναι το άλλο μισό της πρόληψης. Δες τον αναλυτικό <Link to="/blog/tattoo-aftercare-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">οδηγό μας για τη φροντίδα μετά το studio</Link>. Και αν είσαι άρρωστος, δες γιατί <Link to="/blog/boro-tatouaz-an-eimai-arrostos-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">δεν πρέπει να κάνεις τατουάζ όταν είσαι άρρωστος</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες σιγουριά για την <span className="text-pink-500">ασφάλεια και την υγιεινή;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram του στούντιο. Έμπειροι καλλιτέχνες, αυστηρές συνθήκες υγιεινής και ουσιαστική υποστήριξη πριν, κατά τη διάρκεια και μετά τη συνεδρία σου, με τη σιγουριά ότι το τατουάζ σου γίνεται σε ασφαλές περιβάλλον.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-infection-signs-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Καταλαβαίνεις αν ένα Τατουάζ Μολύνθηκε</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Φυσιολογική επούλωση ή μόλυνση και τι να κάνεις.</p>
              </Link>
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Φροντίδα Τατουάζ Μετά το Studio</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρεις οδηγίες aftercare για σωστή επούλωση.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
